import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { getProfileUrl, downloadQrCode, getImageUrl } from '../utils/urlUtils';
import '../styles/AppShell.css';

const QRCodeDialog = ({ open, employee, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!employee) return null;

  const handleDownloadQR = async () => {
    if (!employee.qr_code_url) {
      alert('QR code not available for download');
      return;
    }
    setDownloading(true);
    try {
      await downloadQrCode(employee.qr_code_url, employee.employee_code);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  const qrSrc = employee.qr_code_url ? getImageUrl(employee.qr_code_url) : null;
  const photoSrc = employee.photo_url ? getImageUrl(employee.photo_url) : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <QrCodeIcon color="primary" />
          Identity QR
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Box textAlign="center">
          <Avatar
            src={photoSrc}
            alt={`${employee.first_name} ${employee.last_name}`}
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 1.5,
              border: '3px solid',
              borderColor: 'divider',
              fontWeight: 700,
            }}
          >
            {employee.first_name?.charAt(0)}
            {employee.last_name?.charAt(0)}
          </Avatar>
          <Typography sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
            {employee.first_name} {employee.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employee.designation || employee.position || 'Employee'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 0.8,
              px: 1.2,
              py: 0.3,
              borderRadius: 1,
              bgcolor: 'rgba(11,29,74,0.06)',
              fontFamily: 'monospace',
              fontWeight: 700,
            }}
          >
            {employee.employee_code}
          </Typography>

          {qrSrc ? (
            <Box
              sx={{
                mt: 3,
                p: 2.5,
                display: 'inline-block',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                boxShadow: '0 8px 24px rgba(11,29,74,0.08)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, fontWeight: 650, letterSpacing: '0.06em' }}
              >
                SCAN TO VERIFY
              </Typography>
              <img
                src={qrSrc}
                alt="Employee QR Code"
                style={{ width: 168, height: 168, display: 'block', margin: '0 auto' }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.5, display: 'block', wordBreak: 'break-all', maxWidth: 220 }}
              >
                {getProfileUrl(employee.employee_code)}
              </Typography>
            </Box>
          ) : (
            <Typography color="error" sx={{ mt: 3 }}>
              QR code not available
            </Typography>
          )}

          {qrSrc && (
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={handleDownloadQR}
                disabled={downloading}
              >
                Download
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(`/card/${employee.employee_code}`, '_blank')}
              >
                View card
              </Button>
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeDialog;
